import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/homeData';

export default function BasetaoVsSuperbuy() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-20">
        <article className="max-w-3xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">Comparison</span>
            <h1 className="text-2xl md:text-4xl font-heading text-foreground-950 mb-4">BaseTao vs Superbuy: Detailed Platform Comparison</h1>
            <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">An objective comparison between BaseTao and Superbuy to help you choose the right shopping agent platform.</p>
          </div>

          <div className="prose-custom space-y-8">
            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">Overview of Both Platforms</h2>
              <p className="text-foreground-600 leading-relaxed mb-4">Both <strong className="text-foreground-900">BaseTao</strong> and <strong className="text-foreground-900">Superbuy</strong> are well-established shopping agent platforms that help international buyers purchase products from Chinese marketplaces. While they share the same core function, there are important differences in features, pricing structure, user interface, and warehouse services.</p>
            </section>

            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">Feature Comparison</h2>
              <div className="overflow-hidden rounded-lg border border-background-200/70">
                <table className="w-full text-sm">
                  <thead><tr className="bg-background-100"><th className="text-left px-4 py-2.5 font-heading text-foreground-900 text-xs">Feature</th><th className="text-left px-4 py-2.5 font-heading text-primary-700 text-xs">BaseTao</th><th className="text-left px-4 py-2.5 font-heading text-foreground-600 text-xs">Superbuy</th></tr></thead>
                  <tbody className="divide-y divide-background-200/60">
                    {[{f:'Shipping Options',b:'Multiple carriers available',s:'Multiple carriers available'},{f:'Warehouse Storage',b:'Free storage period included',s:'Free storage period included'},{f:'QC Inspection',b:'Standard inspection with photos',s:'Standard inspection with photos'},{f:'Spreadsheet Workflow',b:'Well-suited for spreadsheet tracking',s:'Compatible with spreadsheets'},{f:'User Interface',b:'Clean and straightforward',s:'Feature-rich with more options'},{f:'Customer Support',b:'Responsive support channels',s:'Responsive support channels'}].map((r,i)=>(<tr key={i}><td className="px-4 py-2.5 text-foreground-700 font-medium text-xs">{r.f}</td><td className="px-4 py-2.5 text-xs"><span className="inline-flex items-center gap-1 text-primary-600"><div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-check-line text-xs"></i></div>{r.b}</span></td><td className="px-4 py-2.5 text-foreground-500 text-xs">{r.s}</td></tr>))}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">Best Use Cases for Each Platform</h2>
              <p className="text-foreground-600 leading-relaxed mb-4"><strong className="text-foreground-900">BaseTao</strong> is particularly strong for shoppers who value a streamlined experience with a spreadsheet-friendly workflow. The platform integrates well with the organizational methods we recommend throughout this website.</p>
              <p className="text-foreground-600 leading-relaxed"><strong className="text-foreground-900">Superbuy</strong> may appeal to users who want a wider range of additional features and a more established community. Both platforms are legitimate options, and the best choice depends on your specific needs.</p>
              <p className="text-foreground-600 leading-relaxed">For more comparisons, see our <Link to="/basetao-alternatives/" className="text-primary-600 hover:text-primary-700 font-medium">BaseTao Alternatives page</Link> where we also cover Sugargoo and CSSBuy.</p>
            </section>

            <div className="bg-background-100 border border-background-200/70 rounded-xl p-6 mt-10">
              <h3 className="font-heading text-lg text-foreground-900 mb-3">Experience BaseTao Yourself</h3>
              <p className="text-foreground-600 text-sm mb-4">The best way to decide is to try the platform. Start browsing products and organize with your spreadsheet.</p>
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 text-background-50 rounded-xl text-sm font-semibold hover:bg-accent-600 transition-colors duration-200 whitespace-nowrap">Visit BaseTao Spreadsheet <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line text-sm"></i></div></a>
            </div>

            <div className="text-sm text-foreground-400 mt-8 pt-6 border-t border-background-200/60 flex flex-wrap gap-4">
              <Link to="/" className="hover:text-primary-600 transition-colors">&larr; Back to Home</Link>
              <Link to="/basetao-alternatives/" className="hover:text-primary-600 transition-colors">All Alternatives</Link>
              <Link to="/basetao-review/" className="hover:text-primary-600 transition-colors">BaseTao Review</Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />

      {/* Schema.org Article */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'BaseTao vs Superbuy: Detailed Platform Comparison',
            description: 'An objective comparison between BaseTao and Superbuy to help you choose the right shopping agent platform.',
            dateModified: '2026-08-10',
            datePublished: '2026-08-10',
            author: {
              '@type': 'Organization',
              name: 'BaseTao Sheet',
            },
            publisher: {
              '@type': 'Organization',
              name: 'BaseTao Sheet',
              logo: {
                '@type': 'ImageObject',
                url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/0dc5bc4081b05862da3d01553c9c9d93.png',
              },
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': 'https://basetaosheet.team/basetao-vs-superbuy/',
            },
          }),
        }}
      />
    </div>
  );
}